/**
 * tm-back-in-stock.js — <tm-back-in-stock> custom element (blocks/tm-back-in-stock.liquid).
 *
 * Lives in its own asset (not the block's {% javascript %}) because the quick-add
 * modal injects the product section into ANY page (collection, home, search): the
 * element must be defined sitewide or it stays inert there. Loaded interaction-only
 * via the Boreal idle loader (snippets/scripts.liquid → assets/boreal-idle-load.js),
 * like tm-size-guide.js. Everything is SSR; this script only toggles state.
 *
 * - `variant:update` (vendor variant-picker) → show/hide + pre-select the variant
 * - trigger click → <dialog> drawer (animated close, Escape, backdrop)
 * - submit → Klaviyo client APIs: back-in-stock subscription, then optional list opt-in
 */
(() => {
const BIS_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const LIST_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
const KLAVIYO_REVISION = '2026-07-15';
const HTML_OPEN_CLASS = 'tm-bis-open';
// Klaviyo asks clients to retry 429/503 with backoff, honouring Retry-After.
const RETRY_STATUSES = new Set([429, 502, 503, 504]);
const MAX_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 1000;

class TmBackInStock extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-tm-bis-trigger]');
    this.dialog = this.querySelector('dialog');
    this.form = this.querySelector('form');
    if (!this.trigger || !this.dialog || !this.form) return;

    this.emailInput = this.form.querySelector('input[type="email"]');
    this.variantField = this.form.querySelector('[data-variant-select]');
    this.newsletterInput = this.form.querySelector('input[name="newsletter"]');
    this.submitButton = this.form.querySelector('button[type="submit"]');
    this.submitLabel = this.form.querySelector('.tm-bis__submit-label');
    this.message = this.form.querySelector('[data-message]');
    this.successBox = this.form.querySelector('[data-success]');

    this.handleOpen = this.open.bind(this);
    this.handleClose = this.close.bind(this);
    this.handleBackdropClick = this.handleBackdropClick.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleNativeClose = this.handleNativeClose.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
    this.handleEditorSelect = this.handleEditorSelect.bind(this);
    this.handleEditorDeselect = this.handleEditorDeselect.bind(this);

    this.trigger.addEventListener('click', this.handleOpen);
    this.querySelectorAll('[data-close]').forEach((button) => button.addEventListener('click', this.handleClose));
    this.dialog.addEventListener('click', this.handleBackdropClick);
    this.dialog.addEventListener('cancel', this.handleCancel);
    this.dialog.addEventListener('keydown', this.handleKeydown);
    this.dialog.addEventListener('close', this.handleNativeClose);
    this.form.addEventListener('submit', this.handleSubmit);

    // The vendor variant-picker dispatches `variant:update` (bubbling) after
    // re-fetching the section; listen on the section (or the quick-add dialog).
    this.scope = this.closest('.shopify-section, dialog') || document;
    this.scope.addEventListener('variant:update', this.handleVariantUpdate);

    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:block:select', this.handleEditorSelect);
      document.addEventListener('shopify:block:deselect', this.handleEditorDeselect);
    }
  }

  disconnectedCallback() {
    if (this.scope) this.scope.removeEventListener('variant:update', this.handleVariantUpdate);
    document.removeEventListener('shopify:block:select', this.handleEditorSelect);
    document.removeEventListener('shopify:block:deselect', this.handleEditorDeselect);
    if (!this.dialog) return;
    this.dialog.removeEventListener('click', this.handleBackdropClick);
    this.dialog.removeEventListener('cancel', this.handleCancel);
    this.dialog.removeEventListener('keydown', this.handleKeydown);
    this.dialog.removeEventListener('close', this.handleNativeClose);
    this.handleNativeClose();
  }

  /* ── Variant sync ─────────────────────────────────────────────── */

  handleVariantUpdate(event) {
    const detail = event.detail || {};
    const data = detail.data || {};
    if (data.productId && data.productId !== this.dataset.productId) return;

    const variant = detail.resource;
    const isSoldOut = !!variant && variant.available === false;

    this.hidden = !isSoldOut;
    this.hiddenByStock = !isSoldOut;
    if (!variant) return;

    this.dataset.variantId = String(variant.id);
    this.selectVariant(String(variant.id));
    this.resetState();
  }

  selectVariant(variantId) {
    const field = this.variantField;
    if (!field) return;

    if (field.tagName !== 'SELECT') {
      field.value = variantId;
      return;
    }

    const option = Array.from(field.options).find((item) => item.value === variantId);
    if (!option || option.disabled) return;
    field.value = variantId;
  }

  /* ── Dialog ───────────────────────────────────────────────────── */

  open() {
    if (!this.dialog || this.dialog.open) return;

    this.dialog.showModal();
    document.documentElement.classList.add(HTML_OPEN_CLASS);

    if (!this.emailInput || this.emailInput.value || !window.matchMedia('(min-width: 750px)').matches) return;
    this.emailInput.focus();
  }

  close() {
    const dialog = this.dialog;
    if (!dialog || !dialog.open || this.isClosing) return;

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      dialog.removeEventListener('animationend', finish);
      dialog.removeEventListener('animationcancel', finish);
      dialog.classList.remove('is-closing');
      this.isClosing = false;
      dialog.close();
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finish();
      return;
    }

    this.isClosing = true;
    dialog.classList.add('is-closing');
    // `animationcancel` + timeout: the animation never "ends" if an ancestor
    // (e.g. the quick-add dialog) gets hidden meanwhile.
    dialog.addEventListener('animationend', finish);
    dialog.addEventListener('animationcancel', finish);
    window.setTimeout(finish, 400);
  }

  handleCancel(event) {
    // Escape: run the closing animation instead of the instant native close.
    event.preventDefault();
    this.close();
  }

  handleKeydown(event) {
    if (event.key !== 'Escape') return;
    // Own the Escape: inside the quick-add modal the keydown would otherwise
    // bubble to the vendor dialog component and close the quick-add too.
    event.preventDefault();
    event.stopPropagation();
    this.close();
  }

  handleBackdropClick(event) {
    if (event.target !== this.dialog) return;
    // A backdrop click targets the <dialog> itself. Inside the quick-add modal
    // the vendor dialog component would read that as "clicked outside" and
    // close the quick-add as well — keep it to this drawer.
    event.stopPropagation();
    this.close();
  }

  handleNativeClose() {
    document.documentElement.classList.remove(HTML_OPEN_CLASS);
  }

  // Theme editor: reveal + open the drawer while the block is selected, even
  // if the current variant is in stock; restore the stock-driven state after.
  handleEditorSelect(event) {
    if (event.target !== this && !this.contains(event.target)) return;
    if (this.hiddenByStock === undefined) this.hiddenByStock = this.hidden;
    this.hidden = false;
    this.open();
  }

  handleEditorDeselect(event) {
    if (event.target !== this && !this.contains(event.target)) return;
    this.close();
    if (this.hiddenByStock !== undefined) this.hidden = this.hiddenByStock;
  }

  /* ── Submit ───────────────────────────────────────────────────── */

  async handleSubmit(event) {
    event.preventDefault();

    const email = this.emailInput ? this.emailInput.value.trim() : '';
    if (!this.emailInput || !email || !this.emailInput.checkValidity()) {
      if (this.emailInput) this.emailInput.reportValidity();
      return;
    }

    const variantId = this.variantField ? this.variantField.value : this.dataset.variantId;
    if (!variantId) {
      this.showMessage(this.dataset.errorGeneric, 'error');
      return;
    }

    this.setBusy(true);
    this.hideMessage();

    let response;
    try {
      response = await this.post(BIS_ENDPOINT, this.buildBackInStockPayload(email, variantId));
    } catch (error) {
      this.setBusy(false);
      this.showMessage(this.dataset.errorGeneric, 'error');
      return;
    }

    if (!response.ok) {
      this.setBusy(false);
      await this.onClientError(response);
      return;
    }

    if (this.newsletterInput && this.newsletterInput.checked && this.dataset.listId) {
      try {
        await this.post(LIST_ENDPOINT, this.buildNewsletterPayload(email));
      } catch (error) {
        // The restock alert is recorded; a failed list opt-in must not block the success state.
      }
    }

    this.setBusy(false);
    this.onSuccess(email, variantId);
  }

  async post(endpoint, payload, attempt = 0) {
    const response = await fetch(`${endpoint}?company_id=${encodeURIComponent(this.dataset.companyId)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify(payload),
    });

    if (!RETRY_STATUSES.has(response.status) || attempt >= MAX_RETRIES) return response;

    // Retry-After is only readable when Klaviyo exposes it; otherwise exponential backoff + jitter.
    const retryAfter = Number(response.headers.get('Retry-After'));
    const wait = Number.isFinite(retryAfter) && retryAfter > 0
      ? retryAfter * 1000
      : RETRY_BASE_DELAY_MS * 2 ** attempt + Math.random() * 250;

    await new Promise((resolve) => window.setTimeout(resolve, wait));
    return this.post(endpoint, payload, attempt + 1);
  }

  buildBackInStockPayload(email, variantId) {
    return {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variantId}`,
            },
          },
        },
      },
    };
  }

  buildNewsletterPayload(email) {
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: this.dataset.source || 'Back in stock',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                subscriptions: {
                  email: {
                    marketing: { consent: 'SUBSCRIBED' },
                  },
                },
              },
            },
          },
        },
        relationships: {
          list: {
            data: { type: 'list', id: this.dataset.listId },
          },
        },
      },
    };
  }

  onSuccess(email, variantId) {
    this.dataset.state = 'success';
    if (this.emailInput) this.emailInput.removeAttribute('aria-invalid');
    if (this.successBox) this.successBox.hidden = false;
    this.identify(email);
    this.dispatchEvent(
      new CustomEvent('tm:back-in-stock:subscribed', {
        bubbles: true,
        detail: { email, variantId, newsletter: !!(this.newsletterInput && this.newsletterInput.checked) },
      })
    );
  }

  async onClientError(response) {
    let isEmailError = false;
    try {
      const body = await response.json();
      const errors = body.errors || [];
      isEmailError = errors.some((item) => {
        const pointer = item.source && item.source.pointer;
        return typeof pointer === 'string' && pointer.endsWith('/email');
      });
      // Surface Klaviyo's error code (e.g. variant_not_found when the catalog is not synced).
      console.warn('[tm-back-in-stock] Klaviyo rejected the request', response.status, errors.map((e) => e.code || e.title || e.detail).join(', '));
    } catch (error) {
      isEmailError = false;
      console.warn('[tm-back-in-stock] Klaviyo rejected the request', response.status);
    }

    this.showMessage(isEmailError ? this.dataset.errorInvalid : this.dataset.errorGeneric, 'error');
    if (!this.emailInput) return;
    this.emailInput.setAttribute('aria-invalid', 'true');
    this.emailInput.focus();
  }

  // Links the browser session to the Klaviyo profile (onsite tracking) when Klaviyo's script is present.
  identify(email) {
    const klaviyo = window.klaviyo;
    if (!klaviyo || typeof klaviyo.identify !== 'function') return;
    try {
      klaviyo.identify({ email });
    } catch (error) {
      // Optional — never block the flow on onsite tracking.
    }
  }

  /* ── State helpers ────────────────────────────────────────────── */

  resetState() {
    if (this.dataset.state !== 'success') return;
    delete this.dataset.state;
    if (this.successBox) this.successBox.hidden = true;
    this.hideMessage();
  }

  setBusy(isBusy) {
    this.toggleAttribute('aria-busy', isBusy);
    if (this.submitButton) this.submitButton.disabled = isBusy;
    if (!this.submitLabel) return;
    if (isBusy) {
      this.submitLabel.dataset.label = this.submitLabel.textContent;
      this.submitLabel.textContent = this.dataset.sendingLabel || this.submitLabel.textContent;
      return;
    }
    if (this.submitLabel.dataset.label) this.submitLabel.textContent = this.submitLabel.dataset.label;
  }

  showMessage(text, type) {
    if (!this.message) return;
    this.message.textContent = text || '';
    this.message.classList.toggle('tm-bis__message--error', type === 'error');
    this.message.hidden = false;
  }

  hideMessage() {
    if (!this.message) return;
    this.message.hidden = true;
    this.message.textContent = '';
  }
}

if (!customElements.get('tm-back-in-stock')) {
  customElements.define('tm-back-in-stock', TmBackInStock);
}
})();
